<template>
  <div>
    <div
      class="login-tfa-form__numbers"
      @paste.prevent="onPaste"
      @keydown="onKeyDown"
    >
      <LoginTfaNumberCell
        v-for="(digit, index) of digits"
        :key="index"
        class="login-tfa-form__number"
        :active="currentIndex === index"
        :value="digit"
      />
    </div>
    <slot
      name="actions"
      :confirm-disabled="confirmDisabled"
    />
    <a
      class="login-tfa-form__help"
      :href="supportLink"
      target="_blank"
      >Get Help</a
    >
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick } from 'vue'
import LoginTfaNumberCell from './LoginTfaNumberCell.vue'

const supportLink = 'https://support.google.com/accounts/answer/185834?hl=en'
const maxChars = 6

const props = defineProps<{ token?: string }>()

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'update:token', value: string): void
}>()

const digits = computed(() => {
  const token = props.token ?? ''
  const chars = token.length
  return [...(token || '').split(''), ...new Array(Math.max(maxChars - chars, 0)).fill(null)]
})

const currentIndex = computed(() => props.token?.length ?? 0)
const confirmDisabled = computed(() => (props.token?.length ?? 0) < maxChars)

const onConfirm = (): void => {
  if (confirmDisabled.value) {
    return
  }
  emit('confirm')
}

const onKeyDown = (event: KeyboardEvent): void => {
  event.stopPropagation()
  if (!event.metaKey && !event.ctrlKey) {
    event.preventDefault()
  }

  if (event.key === 'Enter' && !confirmDisabled.value) {
    onConfirm()
    return
  }

  if (event.key === 'Backspace' && currentIndex.value >= 0 && props.token) {
    emit('update:token', props.token?.slice(0, -1))
    return
  }

  if (event.key.match(/^\d$/) && currentIndex.value < maxChars) {
    const digit = parseInt(event.key, 10)
    const payload = `${props.token ?? ''}${digit}`
    emit('update:token', payload)
    if (payload.length === maxChars) {
      nextTick(() => onConfirm())
    }
  }
}

const onPaste = (event: ClipboardEvent): void => {
  const clipboardData = event.clipboardData?.getData('text')
  if (!clipboardData) {
    return
  }

  const newDigits = clipboardData.split(/\s*/).map((x) => parseInt(x, 10))
  if (newDigits.length !== 6 || newDigits.some(isNaN)) {
    return
  }

  const newToken = newDigits.join('')
  emit('update:token', newToken)
  nextTick(() => onConfirm())
}
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/colors.global.scss';

.login-tfa-form__numbers {
  width: 296px;
  @include row--center;
  justify-content: space-evenly;
  margin: 16px auto 40px auto;
}

.login-tfa-form__number {
  &:nth-child(3) {
    margin-right: 4px;
  }
  &:nth-child(4) {
    margin-left: 4px;
  }
}

.login-tfa-form__help {
  @include row--center;
  @include typography(md-1, inter, 500);
  color: $colorContentPrimary;
  cursor: pointer;
}
</style>
